import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useFetcher, useLocation, useNavigation } from '@remix-run/react';
import { useStore } from '~/context/store-context';

type Product = { id?: unknown };
type ProductPage<T> = { products?: T[]; total?: number; error?: unknown; isNotFound?: boolean };

/** Append route-loader pages without navigating or replacing the current grid. */
export function useInfiniteProducts<T extends Product>(initialProducts: T[], total: number) {
  const location = useLocation();
  const navigation = useNavigation();
  const { selectedStore } = useStore();
  const id = useId();
  const key = `${location.pathname}${location.search}|${selectedStore}`;
  const initialPage = Math.max(1, Number(new URLSearchParams(location.search).get('page')) || 1);
  const fetcher = useFetcher<ProductPage<T>>({ key: `${id}:${key}` });
  const [state, setState] = useState({
    key,
    source: initialProducts,
    items: initialProducts,
    page: initialPage,
    ended: false,
    error: false,
  });
  const pending = useRef<{
    key: string;
    source: T[];
    page: number;
    previous: unknown;
    started: boolean;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const current = state.key === key && state.source === initialProducts;
  const items = current ? state.items : initialProducts;
  const page = current ? state.page : initialPage;
  const error = current && state.error;
  const hasMore = items.length > 0 && !(current && state.ended) && page * 18 < total;

  useEffect(() => {
    pending.current = null;
    setBusy(false);
    setState({
      key,
      source: initialProducts,
      items: initialProducts,
      page: initialPage,
      ended: false,
      error: false,
    });
  }, [key, initialProducts, initialPage]);

  useEffect(() => {
    const request = pending.current;
    if (!request || request.key !== key || request.source !== initialProducts) return;
    if (fetcher.state !== 'idle') {
      request.started = true;
      return;
    }
    if (!request.started && fetcher.data === request.previous) return;
    pending.current = null;
    setBusy(false);
    // These catalogue loaders return the same JSON product shape as the initial page.
    const data = fetcher.data as ProductPage<T> | undefined;
    if (!data || !Array.isArray(data.products) || data.error || data.isNotFound) {
      setState((prev) => ({ ...prev, error: true }));
      return;
    }
    setState((prev) => {
      const seen = new Set(prev.items.map((product) => String(product.id)));
      const additions = data.products!.filter((product) => {
        const id = String(product.id);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      return {
        ...prev,
        items: [...prev.items, ...additions],
        page: request.page,
        ended: data.products!.length === 0 || additions.length === 0,
        error: false,
      };
    });
  }, [fetcher.state, fetcher.data, key, initialProducts]);

  const loadMore = useCallback(() => {
    if (pending.current || !current || !hasMore || navigation.state !== 'idle') return;
    const params = new URLSearchParams(location.search);
    params.set('page', String(page + 1));
    if (location.pathname === '/') params.set('index', '');
    pending.current = {
      key,
      source: initialProducts,
      page: page + 1,
      previous: fetcher.data,
      started: false,
    };
    setBusy(true);
    setState((prev) => ({ ...prev, error: false }));
    fetcher.load(`${location.pathname}?${params}`);
  }, [
    current,
    hasMore,
    navigation.state,
    location.search,
    location.pathname,
    page,
    key,
    initialProducts,
    fetcher,
  ]);

  return {
    items,
    loadMore,
    hasMore,
    loading: busy && current,
    error,
    paused: navigation.state !== 'idle',
    startIndex: (initialPage - 1) * 18,
  };
}
